import { useState, useEffect } from 'react';
import logo from '@/assets/matou-bird-logo-black(1).png';
import paddle1 from '@/assets/paddle1.svg';
import paddle2 from '@/assets/paddle2.svg';
import paddle3 from '@/assets/paddle3.svg';
import macIcon from '@/assets/mac.png';
import winIcon from '@/assets/win.png';
import linIcon from '@/assets/lin.png';

interface DownloadLink {
  label: string;
  href: string;
}

interface Paddle {
  id: number;
  installer: string;
  src: string;
  icon: string;
  height: number;
  iconLeft: string;
  downloads: DownloadLink[];
}

function InlineSvg({ src, alt, style, className }: { src: string; alt: string; style?: React.CSSProperties; className?: string }) {
  const [svg, setSvg] = useState('');
  useEffect(() => {
    fetch(src).then(r => r.text()).then(setSvg);
  }, [src]);
  return <div aria-label={alt} style={style} className={className} dangerouslySetInnerHTML={{ __html: svg }} />;
}

async function fetchLatestRelease(): Promise<string | null> {
  try {
    const response = await fetch(
      'https://api.github.com/repos/matou-collective/matou-app/releases/latest'
    );
    if (!response.ok) {
      throw new Error('Failed to fetch releases');
    }
    const release = await response.json();
    return release.tag_name ?? null;
  } catch (error) {
    console.error('Error fetching latest release:', error);
    return null;
  }
}

export default function Download() {
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const [latestTag, setLatestTag] = useState<string | null>(null);

  useEffect(() => {
    fetchLatestRelease().then(setLatestTag);
  }, []);

  const navItems = [
    { label: 'Github', href: 'https://github.com/matou-collective/matou-app/' },
    { label: 'Website', href: 'https://matou.nz' },
    { label: 'Docs', href: 'https://docs.matou.nz' },
    { label: 'Open Collective', href: 'https://opencollective.com/matoucollective/contribute' },
  ];

  // Generate download URLs based on latest tag
  const getDownloadUrls = (): Paddle[] => {
    const tag = latestTag || 'v0.0.1';
    const version = tag.replace(/^v/, '');
    const baseUrl = `https://github.com/matou-collective/matou-app/releases/download/${tag}`;

    return [
      {
        id: 1,
        installer: 'Mac',
        src: paddle1,
        icon: macIcon,
        height: 970,
        iconLeft: '52%',
        downloads: [
          { label: '.dmg (ARM64)', href: `${baseUrl}/matou-${version}-darwin-arm64.dmg` },
          { label: '.dmg (x64)', href: `${baseUrl}/matou-${version}-darwin-x64.dmg` },
        ],
      },
      {
        id: 2,
        installer: 'Windows',
        src: paddle2,
        icon: winIcon,
        height: 1320,
        iconLeft: '47%',
        downloads: [
          { label: '.exe', href: `${baseUrl}/matou-${version}-win32.exe` },
        ],
      },
      {
        id: 3,
        installer: 'Linux',
        src: paddle3,
        icon: linIcon,
        height: 956,
        iconLeft: '50%',
        downloads: [
          { label: '.AppImage', href: `${baseUrl}/matou-${version}-linux.AppImage` },
        ],
      },
    ];
  };

  const paddles = getDownloadUrls();

  return (
    <div className="w-full h-full bg-[#f5f5f0] relative overflow-hidden" style={{ fontFamily: "'Antonio', sans-serif" }}>
      {/* Logo */}
      <div className="absolute top-8 left-8">
        <img src={logo} alt="Mātou" width="60" height="60" />
      </div>

      {/* Navigation Menu */}
      <div className="absolute top-8 right-8 flex flex-col items-end gap-3">
        {navItems.map((item) => (
          <a
            key={item.label}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 cursor-pointer relative no-underline text-inherit"
            onMouseEnter={() => setHoveredNav(item.label)}
            onMouseLeave={() => setHoveredNav(null)}
          >
            {/* Label and Line Container */}
            <div className="relative flex flex-col items-end">
              {/* Label */}
              <span
                className="text-lg whitespace-nowrap transition-all duration-300"
                style={{
                  opacity: hoveredNav === item.label ? 1 : 0,
                  transform: hoveredNav === item.label ? 'translateX(0)' : 'translateX(20px)',
                }}
              >
                {item.label}
              </span>

              {/* Line - extends 20px beyond text */}
              <div
                className="h-[2px] bg-black mt-1 transition-all duration-300"
                style={{
                  width: hoveredNav === item.label ? 'calc(100% + 20px)' : 0,
                  transformOrigin: 'right',
                }}
              />
            </div>

            {/* Dot */}
            <div
              className="w-6 h-6 rounded-full border-2 border-black flex-shrink-0 transition-colors duration-300"
              style={{
                backgroundColor: hoveredNav === item.label ? '#000' : 'transparent',
              }}
            />
          </a>
        ))}
      </div>

      {/* Main Content */}
      <div className="w-full h-full flex items-end justify-between">
        {/* Left Side - Text */}
        <div className="flex flex-col p-[50px]">
          <h2 className="text-6xl pb-[50px]">Downloads</h2>
          <h1 className="text-[240px] font-bold leading-none tracking-tight">MĀTOU</h1>
        </div>

        {/* Right Side - Paddles */}
        <div className="flex items-end gap-16 pr-[50px]">
          {paddles.map((paddle) => (
            <a
              key={paddle.id}
              href={paddle.downloads[0].href}
              className="relative group cursor-pointer overflow-hidden transition-transform duration-300 hover:scale-105 block"
              style={{ height: paddle.height * 0.6 }}
            >
              <InlineSvg src={paddle.src} alt={paddle.installer} className="w-auto [&>svg]:h-full [&>svg]:w-auto" style={{ height: paddle.height }} />
              <div
                className="absolute top-[15%] -translate-x-1/2 z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center"
                style={{ left: paddle.iconLeft }}
              >
                <img src={paddle.icon} alt={paddle.installer} className="w-16 h-16" />
                <span className="text-white text-base font-medium mt-2">{paddle.installer}</span>
                {paddle.downloads.map((dl, i) => (
                  <div key={dl.label} className="flex flex-col items-center">
                    {i > 0 && <div className="w-12 h-[1px] bg-white/40 my-3" />}
                    {i > 0 ? (
                      <a
                        href={dl.href}
                        onClick={(e) => e.stopPropagation()}
                        className="text-white/60 text-sm hover:text-white transition-colors pointer-events-auto"
                      >
                        {dl.label}
                      </a>
                    ) : (
                      <span className="text-white/60 text-sm">{dl.label}</span>
                    )}
                  </div>
                ))}
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
